import {
  AspectRatio,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxOptions,
  ComboboxTarget,
  Image,
  ScrollArea,
  useCombobox,
  Drawer,
} from '@mantine/core'
import {
  Competition,
  CompetitionSelectItem,
  useActiveCompetitions,
} from './api'
import { IconChevronDown, IconCircleCheck } from '@tabler/icons-react'
import classes from './Navigation.module.css'
import { LeaguestarState } from '../state'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Typography } from '../common/typography'
import { useCompetitionId } from '../common/hooks'

export default function CompetitionSelect({
  isMobile,
  setMobileNavigationOpen,
}: {
  isMobile: boolean
  setMobileNavigationOpen?: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const competition_id = useCompetitionId()
  const navigate = useNavigate()
  const location = useLocation()
  const { data, isSuccess } = useActiveCompetitions()

  const active_competitions = data?.competitions || []

  useEffect(() => {
    if (isSuccess && active_competitions?.length) {
      LeaguestarState.update((state) => {
        // Auto-select first competition if none is selected
        if (competition_id == null || competition_id === 0) {
          state.competition_id = active_competitions[0].competition_id
        }
      })
    }
  }, [isSuccess, active_competitions, competition_id])

  const competition_data: CompetitionSelectItem[] = active_competitions.map(
    (competition: Competition) => ({
      value: competition.competition_id.toString(),
      label: competition.name,
      logo: competition.logo.mini,
      logo_selected: competition.logo.thumbnail,
    })
  )

  const selectedCompetition = competition_data.find(
    (item) => item.value === competition_id.toString()
  )

  const handleCompetitionChange = (value: string | null) => {
    if (!value) return

    const id = parseInt(value, 10)
    if (isNaN(id)) return

    if (!Array.isArray(active_competitions)) return

    const newCompetition = active_competitions.find(
      (c) => c.competition_id === id
    )

    LeaguestarState.update((state) => {
      state.competition_id = id
    })

    // Smart redirect: stay on page if only competitionId, redirect to parent if entity-specific
    const currentPath = location.pathname
    const competitionRouteMatch = currentPath.match(
      /^\/competitions\/\d+\/(.*)$/
    )

    if (competitionRouteMatch) {
      const restOfPath = competitionRouteMatch[1]
      const kindRoute = newCompetition?.kind === 'race' ? 'races' : 'matches'

      // Detail pages with entity-specific IDs should redirect to parent
      const detailPagePatterns = [
        { pattern: /^(matches|races)\/\d+/, parent: kindRoute },
        { pattern: /^teams\/\d+/, parent: 'teams' },
        { pattern: /^players\/\d+/, parent: 'players' },
      ]

      for (const { pattern, parent } of detailPagePatterns) {
        if (pattern.test(restOfPath)) {
          navigate(`/competitions/${id}/${parent}`)
          return
        }
      }

      // Swap races <-> matches list views if competition kind changed
      if (restOfPath === 'races' || restOfPath === 'matches') {
        navigate(`/competitions/${id}/${kindRoute}`)
        return
      }

      // Stay on current page (just swap competition ID)
      navigate(`/competitions/${id}/${restOfPath}`)
      return
    }

    // Default fallback
    navigate(`/competitions/${id}/home`)
  }

  const targetRow = (opened: boolean, onClick: () => void) => (
    <div
      className={classes.selected_option_row}
      onClick={onClick}
      data-opened={opened}
    >
      <AspectRatio ratio={1} className={classes.aspect_container}>
        <Image
          src={selectedCompetition?.logo_selected}
          alt={selectedCompetition?.label}
          className={classes.selected_competition_logo}
          fit="scale-down"
        />
      </AspectRatio>
      <Typography
        fc={{
          light: 'gray-7',
          dark: 'gray-4',
        }}
      >
        {selectedCompetition?.label}
      </Typography>

      <IconChevronDown data-expanded={opened} className={classes.chevron} />
    </div>
  )

  const optionRow = (item: CompetitionSelectItem, isSelected: boolean) => (
    <div className={classes.select_option_row} data-selected={isSelected}>
      <div className={classes.select_option_row_wrapper}>
        <AspectRatio ratio={1}>
          <Image
            src={item.logo}
            alt={item.label}
            className={classes.competition_logo}
            fit="scale-down"
          />
        </AspectRatio>
        <Typography
          fc={{
            light: isSelected ? 'gray-9' : 'gray-7',
            dark: isSelected ? 'gray-9' : 'gray-4',
          }}
        >
          {item.label}
        </Typography>
      </div>
      {isSelected && <IconCircleCheck className={classes.select_option_icon} />}
    </div>
  )

  if (isMobile) {
    return (
      <>
        {targetRow(mobileDrawerOpen, () => setMobileDrawerOpen(true))}
        {/* Mount Drawer only when open — Mantine 8.3.13 has a bug where the
            Drawer DOM stays visible after `opened` flips to false if the
            parent re-renders around the same time (e.g. navigate() during
            option submit). Unmounting sidesteps it. */}
        {mobileDrawerOpen && (
          <Drawer
            opened
            onClose={() => setMobileDrawerOpen(false)}
            title="Select competition"
            position="bottom"
            size="xs"
            overlayProps={{
              backgroundOpacity: 0.3,
              blur: 2,
            }}
            classNames={{
              title: classes.competition_drawer_title,
              body: classes.competition_drawer_body,
            }}
          >
            {competition_data.map((item) => {
              const isSelected = item.value === competition_id.toString()
              return (
                <div
                  key={item.value}
                  className={classes.drawer_combobox_option}
                  onClick={() => {
                    handleCompetitionChange(item.value)
                    setMobileDrawerOpen(false)
                    setMobileNavigationOpen?.(false)
                  }}
                >
                  {optionRow(item, isSelected)}
                </div>
              )
            })}
          </Drawer>
        )}
      </>
    )
  }

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        handleCompetitionChange(val)
        combobox.closeDropdown()
      }}
      withinPortal={false}
    >
      <ComboboxTarget>
        {targetRow(combobox.dropdownOpened, () => combobox.toggleDropdown())}
      </ComboboxTarget>
      <ComboboxDropdown className={classes.select_dropdown}>
        <ScrollArea.Autosize type="scroll" mah={250}>
          <ComboboxOptions>
            {competition_data.map((item) => {
              const isSelected = item.value === competition_id.toString()
              return (
                <ComboboxOption
                  value={item.value}
                  key={item.value}
                  className={classes.combobox_option}
                >
                  {optionRow(item, isSelected)}
                </ComboboxOption>
              )
            })}
          </ComboboxOptions>
        </ScrollArea.Autosize>
      </ComboboxDropdown>
    </Combobox>
  )
}
